import { html } from 'lit-element';

import { SimpleLitElement } from '@elab/components';

/**
 * Component is used to change quantity of products whose user wants to buy
 *
 * @author Zhynzher Oleksandr
 *
 * @attr { String }  [product-code] - unique code of the product
 * @attr { Number }  [product-wants] - how many products user wants to buy
 * @attr { Number }  [product-stock-level] - how many products in the stock
 * @attr { Number }  [product-min-order-quantity] - minimum value that could be added to the basket
 * @attr { Number }  [product-max-order-quantity] - maximum value that could be added to the basket
 *
 * HTML Structure Example
 *
 * <e2-wishlist-product-quantity
 *      product-code="Product code"
 *      product-wants="Product wants"
 *      product-stock-level="Product stock level"
 *      product-min-order-quantity="Min order quantity"
 *      product-max-order-quantity="Max order quantity"
 * </e2-wishlist-product-quantity>
 * */

export class WishlistProductQuantity extends SimpleLitElement {
    static get properties() {
        return {
            productCode: { type: String, attribute: 'product-code' },
            productWants: { type: Number, attribute: 'product-wants' },
            productStockLevel: {
                type: Number,
                attribute: 'product-stock-level',
            },
            productMinOrderQuantity: {
                type: Number,
                attribute: 'product-min-order-quantity',
            },
            productMaxOrderQuantity: {
                type: Number,
                attribute: 'product-max-order-quantity',
            },
        };
    }

    constructor() {
        super();
        this.productCode = '';
        this.productWants = 1;
        this.productStockLevel = 0;
        this.productMinOrderQuantity = 1;
        this.productMaxOrderQuantity = 15;
    }

    get addToBasketQuantity() {
        return this.productStockLevel
            ? Math.max(this.productWants, this.productMinOrderQuantity)
            : '';
    }

    get selectOptions() {
        const maxOptionsLengthByRestrictions =
            this.productMaxOrderQuantity - this.productMinOrderQuantity + 1;
        const maxOptionsLengthByStock = this.productStockLevel - this.productMinOrderQuantity + 1;
        const optionsLength = Math.min(maxOptionsLengthByRestrictions, maxOptionsLengthByStock);
        const positiveOptionsLength = Math.max(0, optionsLength);

        return [...Array(positiveOptionsLength)].map((_, index) => {
            const quantity = index + this.productMinOrderQuantity;

            return { value: quantity, label: quantity };
        });
    }

    render() {
        return html`
            <div class="wishlist-product-quantity">
                <e2-wishlist-quantity-select
                    .options="${this.selectOptions}"
                    selected="${this.addToBasketQuantity}"
                    product-code="${this.productCode}"
                >
                </e2-wishlist-quantity-select>
            </div>
        `;
    }
}

export default () => customElements.define('e2-wishlist-product-quantity', WishlistProductQuantity);
